import React from 'react';
import { useLingui } from '@lingui/react/macro';
import { Box, SxProps, Theme, Tooltip } from '@mui/material';

import { ApprovalTargetLockState } from '@watershed/constants/dataGovApprovals';

import { useDatasourcePageContext } from '../../../utils/dataIngestion/DatasourcePageContext';
import { useMeasurementProjectContext } from '../../../utils/dataIngestion/MeasurementProjectContext';

/**
 * Returns a tooltip text for a project that is locked. This requires a
 * MeasurementProjectContext.
 *
 * You'll probably want to use useTaskOrProjectLockedTooltip instead, to both
 * check project and task lock status. However, there are some places where we
 * intentionally don't want to check task lock status (like the task unlock button).
 *
 * @param messageOverrides - Optional overrides for the tooltip text.
 * @returns The tooltip text, or undefined if the project is not locked.
 */
export function useProjectLockedTooltip({
  messageOverrides,
}: {
  messageOverrides?: {
    projectClosed?: string;
  };
} = {}): string | undefined {
  const { t } = useLingui();
  const { isMeasurementProjectActive } = useMeasurementProjectContext();

  if (isMeasurementProjectActive) {
    return undefined;
  }

  if (messageOverrides?.projectClosed) {
    return messageOverrides.projectClosed;
  }
  return t({
    message: 'This measurement project is closed.',
    context:
      'Tooltip explaining why an action is disabled because project is closed',
  });
}

/**
 * Returns a tooltip text for a task or project that is locked. This requires a
 * MeasurementProjectContext and a DatasourcePageContext.
 *
 * @param messageOverrides - Optional overrides for the tooltip text.
 * @returns The tooltip text, or undefined if the task or project is not locked.
 */
export function useTaskOrProjectLockedTooltip({
  messageOverrides,
}: {
  messageOverrides?: {
    projectClosed?: string;
    taskLocked?: string;
  };
} = {}): string | undefined {
  const { t } = useLingui();
  const { userUploadTaskLockState } = useDatasourcePageContext();

  const projectLockedTooltip = useProjectLockedTooltip({
    messageOverrides: {
      projectClosed: messageOverrides?.projectClosed,
    },
  });
  if (projectLockedTooltip) {
    return projectLockedTooltip;
  }

  const isTaskLocked =
    userUploadTaskLockState !== ApprovalTargetLockState.Unlocked;
  if (isTaskLocked) {
    return (
      messageOverrides?.taskLocked ||
      t({
        message:
          'This task is submitted. Undo submission or reject task to edit.',
        context:
          'Tooltip explaining why an action is disabled because task is locked',
      })
    );
  }

  return undefined;
}

export function TaskOrProjectLockedTooltip({
  children,
  placement = 'top',
  messageOverrides,
  sx,
}: {
  children:
    | React.ReactElement
    | ((isTaskOrProjectLocked: boolean) => React.ReactElement);
  placement?: 'top' | 'left' | 'right' | 'bottom';
  messageOverrides?: {
    projectClosed?: string;
    taskLocked?: string;
  };
  sx?: SxProps<Theme>;
}) {
  const tooltipText = useTaskOrProjectLockedTooltip({ messageOverrides });
  const isTaskOrProjectLocked = !!tooltipText;

  return (
    <Tooltip title={tooltipText} placement={placement}>
      <Box sx={sx}>
        {typeof children === 'function'
          ? children(isTaskOrProjectLocked)
          : children}
      </Box>
    </Tooltip>
  );
}
